import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { Drawer } from '@mui/material';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Box } from '@vk-hr-tek/ui/Box';
import { Button } from '@vk-hr-tek/ui/Button';
import { Link } from '@vk-hr-tek/ui/Link';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { AnalyticService, GoalsEnum } from '@vk-hr-tek/core/analytics';

import { NavigationExternalItem } from '@app/router';
import { useIsWidget, useSelector } from '@app/hooks';
import { UserRoleEnum } from '@app/types';

import { selectServiceUnavailable, selectSideMenu } from '../../slice';
import { PageError } from '../../page/PageError';
import { useStoreReset } from '../../hooks';
import { selectStatus, selectUser } from '../../../user';
import { Logo } from '../Logo';
import { AbilityProvider } from '../../AbilityProvider';
import { MenuItem, sidebarItemMapper } from '../Sidebar/helpers';

export const MobileMenu = () => {
  const { pathname } = useLocation();
  const { resetStore } = useStoreReset();

  const analyticService = useInject(AnalyticService);

  const isServiceUnavailable = useSelector(selectServiceUnavailable);
  const userPermissionsStatus = useSelector(selectStatus);
  const sideMenu = useSelector(selectSideMenu) as MenuItem[];
  const user = useSelector(selectUser);

  const [drawerOpened, setDrawerOpened] = useState<boolean>(false);
  const [additionalOptions] = useState<
    Record<UserRoleEnum, NavigationExternalItem[]> | undefined
  >(undefined);

  const isWidget = useIsWidget();
  const isSkeletonLoading = userPermissionsStatus === 'loading';

  const handleSetDrawerOpened = (state: boolean) => () => {
    setDrawerOpened(state);
  };

  const handleIsActive = (path: string, href: string): boolean => {
    if (href === '/') {
      return path === href;
    } else if (href === '/settings') {
      return (
        path === href ||
        path.includes('/event-types') ||
        path.includes('/settings')
      );
    } else if (path.startsWith('/personal') && href !== '/news') {
      const currentUserId = pathname.split('/personal/')[1] || '';
      return user?.id !== currentUserId;
    } else {
      return path === href;
    }
  };

  const pageMetaTitle =
    typeof document === 'object' &&
    document !== null &&
    !Array.isArray(document) &&
    document?.title
      ? document.title.split('-')
      : [];

  const handleLogAction = (title: string) => {
    analyticService.registerAction(GoalsEnum.openLink, {
      page: pageMetaTitle[pageMetaTitle.length - 1] || '',
      element: 'Боковое меню',
      block: 'Боковое меню',
      actionType: `Переход на страницу ${title}`,
    });

    if (isServiceUnavailable) {
      return <PageError status={503} />;
    }
  };

  const onMenuItemClick = (pageTitle: string) => {
    resetStore();
    handleLogAction(pageTitle);
    setDrawerOpened(false);
  };

  const urlWithPrefix = (url: string) => {
    if (isWidget) {
      return `${window.REACT_APP_VKHRTEK_WIDGET_STATIC_URL}${url}`;
    }
    return url;
  };

  const getItemRole = (menuItem: MenuItem): UserRoleEnum => {
    if ('nick' in menuItem && menuItem.nick) {
      return menuItem.nick.startsWith('company')
        ? UserRoleEnum.Company
        : UserRoleEnum.Employee;
    }
    return UserRoleEnum.Employee;
  };

  const renderSidebarItem = (menuItem: MenuItem) => {
    const mapper = sidebarItemMapper[menuItem.type];

    if (!mapper) {
      return null;
    }

    const role = getItemRole(menuItem);

    return (
      <AbilityProvider role={role}>
        {mapper({
          item: menuItem,
          sidebarOpened: true,
          isSkeletonLoading,
          isActive: handleIsActive,
          pathname,
          additionalOptions,
          onMenuItemClick,
        })}
      </AbilityProvider>
    );
  };

  useEffect(() => {
    if (drawerOpened) {
      handleSetDrawerOpened(false)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Box>
      <Button
        size="medium"
        variant="tertiaryBlue"
        onClick={handleSetDrawerOpened(true)}
        className="aqa_mobileMenuBurger"
        icon={<Icon name="menu_outline" size="24" />}
      />
      <Drawer
        open={drawerOpened}
        onClose={handleSetDrawerOpened(false)}
        anchor="left"
      >
        <Box
          maxWidth={324}
          boxSizing="border-box"
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
        >
          <Box
            p="16"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              size="medium"
              variant="tertiaryBlue"
              className="aqa_mobileMenuClose"
              onClick={handleSetDrawerOpened(false)}
              icon={
                <Icon
                  name="cross_large_outline"
                  size="20"
                  color="original.brand.primary"
                />
              }
            />

            <Box
              display="flex"
              alignItems="center"
              className="aqa_mobileMenuLogo"
            >
              <Link
                {...(isWidget ? { href: urlWithPrefix('/') } : { to: '/' })}
                onClick={() => onMenuItemClick('Главная')}
              >
                <Logo variant="24" />
              </Link>
            </Box>
          </Box>

          <Box flex={1} overflow="auto" p="16">
            <Box display="flex" flexDirection="column" gap="8">
              {sideMenu && sideMenu.map(renderSidebarItem)}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};
